// "Signing in" on My profiles: a password (optional for someone who signs in with emailed
// links), passkeys, and an optional authenticator app with backup codes. Turning the app off
// or making new backup codes asks for a current code (the API insists: a stolen session can't
// strip it).

import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { ApiError, api, type CodeInput, type PersonSecurity } from "../lib/api";
import { PasskeyError, addPasskey, passkeysSupported, prepareAdd } from "../lib/personPasskeys";

/** The API's rule for test-taker passwords (services/accounts.ts). */
const MIN_PASSWORD = 8;
const formatDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const failed = (err: unknown, fallback: string) => (err instanceof ApiError && err.status > 0 && err.status < 500 ? err.message : fallback);
/** "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP" */
const groupKey = (secret: string) => (secret.match(/.{1,4}/g) ?? []).join(" ");

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-[16px] border border-line bg-panel p-4">
      <h3 className="text-[16px] font-semibold">{title}</h3>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Message({ text, alert = false }: { text: string | null; alert?: boolean }) {
  if (!text) return null;
  return (
    <p role={alert ? "alert" : "status"} className={`mt-3 text-[14px] leading-snug ${alert ? "text-danger" : "text-muted"}`}>
      {text}
    </p>
  );
}

export function MySignIn() {
  const [security, setSecurity] = useState<PersonSecurity | null>(null);
  const load = () =>
    api
      .security()
      .then(setSecurity)
      .catch(() => undefined);
  useEffect(() => {
    void load();
  }, []);
  if (!security) return null;
  return (
    <section className="mt-10" aria-labelledby="sign-in-title" data-testid="my-sign-in">
      <h2 id="sign-in-title" className="heading text-[21px]">
        Signing in
      </h2>
      <p className="text-pretty mt-2 text-[15px] leading-snug text-muted">
        You can always get a sign‑in link by email. Add a passkey to sign in with Face ID or Touch ID, or an authenticator app for a code after
        your password.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <PasswordBlock hasPassword={security.hasPassword} onChanged={load} />
        <PasskeysBlock passkeys={security.passkeys} totpOn={security.totp.on} onChanged={load} />
        <AuthenticatorBlock totp={security.totp} onChanged={load} />
        <OtherDevicesBlock />
      </div>
    </section>
  );
}

/** Signs every other phone or computer out (this one stays signed in). */
function OtherDevicesBlock() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; alert: boolean } | null>(null);
  const signOutOthers = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const { signedOut } = await api.signOutOthers();
      setMessage({
        text: signedOut === 0 ? "You weren't signed in anywhere else." : `Signed out of ${signedOut} other ${signedOut === 1 ? "device" : "devices"}.`,
        alert: false,
      });
    } catch (err) {
      setMessage({ text: failed(err, "That didn't work. Try again."), alert: true });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Block title="Other devices">
      <p className="text-pretty text-[15px] leading-snug">Signed in on a phone or computer you no longer use, or think someone else got in? Sign everywhere else out.</p>
      <button type="button" className="btn btn-secondary mt-3 w-full sm:w-auto sm:self-start sm:px-6" disabled={busy} onClick={() => void signOutOthers()}>
        {busy ? "Signing out…" : "Sign out of other devices"}
      </button>
      <Message text={message?.text ?? null} alert={message?.alert} />
    </Block>
  );
}

function PasswordBlock({ hasPassword, onChanged }: { hasPassword: boolean; onChanged: () => void }) {
  const id = useId();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (hasPassword) {
    return (
      <Block title="Password">
        <p className="text-[15px] leading-snug">Set. To change it, use “Forgot your password?” on the sign-in page.</p>
      </Block>
    );
  }
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    setBusy(true);
    try {
      await api.choosePassword(password);
      onChanged();
    } catch (err) {
      setError(failed(err, "We couldn't save that. Try again."));
      setBusy(false);
    }
  };
  return (
    <Block title="Password">
      <p className="text-pretty text-[15px] leading-snug">Not set. You've been signing in with emailed links, which is fine. A password is optional.</p>
      <form className="mt-3 flex max-w-[420px] flex-col gap-2" onSubmit={save} noValidate>
        <label htmlFor={id} className="text-[14px] font-semibold">
          Choose a password
        </label>
        <input id={id} type="password" autoComplete="new-password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} />
        <button type="submit" className="btn btn-secondary w-full sm:w-auto sm:self-start sm:px-6" disabled={busy}>
          {busy ? "Saving…" : "Save password"}
        </button>
      </form>
      <Message text={error} alert />
    </Block>
  );
}

function PasskeysBlock({ passkeys, totpOn, onChanged }: { passkeys: PersonSecurity["passkeys"]; totpOn: boolean; onChanged: () => void }) {
  const [supported] = useState(passkeysSupported);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; alert: boolean } | null>(null);
  // With the authenticator app on, a code first (the API asks for one); then the passkey.
  const [step, setStep] = useState<"idle" | "code" | "ready">(totpOn ? "idle" : "ready");

  useEffect(() => {
    if (supported && !totpOn) void prepareAdd().catch(() => undefined);
  }, [supported, totpOn]);

  const add = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await addPasskey();
      setMessage({ text: "Passkey added. Next time, tap “Sign in with a passkey”.", alert: false });
      if (totpOn) setStep("idle");
      onChanged();
    } catch (err) {
      setMessage({ text: err instanceof PasskeyError ? err.message : failed(err, "The passkey wasn't added. Try again."), alert: true });
      if (totpOn) setStep("idle");
      else void prepareAdd().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    setMessage(null);
    try {
      await api.removePasskey(id);
      setMessage({ text: "Passkey removed. You can also delete it from your device's password settings.", alert: false });
      onChanged();
    } catch (err) {
      setMessage({ text: failed(err, "We couldn't remove that passkey. Try again."), alert: true });
    }
  };

  return (
    <Block title="Passkeys">
      {passkeys.length === 0 ? (
        <p className="text-pretty text-[15px] leading-snug">Sign in with Face ID, Touch ID or your device's screen lock. No password to type.</p>
      ) : (
        <ul className="flex max-w-[520px] flex-col gap-2" aria-label="Your passkeys">
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 rounded-[12px] border border-line px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold">{p.name || "Passkey"}</p>
                <p className="text-[13px] text-muted">Added {formatDate(p.createdAt)}</p>
              </div>
              <button type="button" className="btn btn-secondary min-h-11 shrink-0 px-3 text-[12px]" onClick={() => void remove(p.id)} aria-label={`Remove ${p.name || "passkey"}`}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {!supported ? null : step === "code" ? (
        <CodeConfirm
          label="Continue"
          onCancel={() => setStep("idle")}
          onConfirm={async (input) => {
            await prepareAdd(input);
            setStep("ready");
          }}
        />
      ) : step === "idle" ? (
        <button type="button" className="btn btn-secondary mt-3 w-full sm:w-auto sm:self-start sm:px-6" onClick={() => setStep("code")}>
          Add a passkey
        </button>
      ) : (
        <>
          {totpOn ? <p className="mt-3 text-[14px] text-muted">Code accepted. Now add the passkey on this device.</p> : null}
          <button type="button" className="btn btn-secondary mt-3 w-full sm:w-auto sm:self-start sm:px-6" disabled={busy} onClick={() => void add()}>
            {busy ? "Waiting for your device…" : totpOn ? "Add the passkey" : "Add a passkey"}
          </button>
        </>
      )}
      {supported ? null : (
        <p className="mt-3 text-[14px] text-muted">This browser can't make passkeys.</p>
      )}
      <Message text={message?.text ?? null} alert={message?.alert} />
    </Block>
  );
}

function BackupCodeList({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className="text-pretty text-[14px] leading-snug">Each works once if you lose your phone. This is the only time they're shown.</p>
      <ol className="mt-3 grid max-w-[420px] grid-cols-2 gap-1.5 rounded-[12px] border border-line p-3 font-mono text-[14px]" aria-label="Backup codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <button
        type="button"
        className="btn btn-secondary mt-2 min-h-11 w-full text-[12px]"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(codes.join("\n"));
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? "Copied" : "Copy codes"}
      </button>
      <label className="mt-3 flex min-h-11 items-center gap-3 text-[15px]">
        <input type="checkbox" className="h-6 w-6 accent-accent-text" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        I've saved these
      </label>
      <button type="button" className="btn btn-primary mt-2 w-full sm:w-auto sm:self-start sm:px-6" disabled={!saved} onClick={onDone}>
        Done
      </button>
    </div>
  );
}

/** A code from the app (or a backup code) for turning it off or replacing the backup codes. */
function CodeConfirm({ label, onConfirm, onCancel }: { label: string; onConfirm: (input: CodeInput) => Promise<void>; onCancel: () => void }) {
  const id = useId();
  const [useBackup, setUseBackup] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const v = value.trim();
    if (useBackup ? !v : !/^\d{6}$/.test(v)) return setError(useBackup ? "Enter a backup code." : "Enter the 6-digit code from your app.");
    setBusy(true);
    try {
      await onConfirm(useBackup ? { backupCode: v } : { code: v });
    } catch (err) {
      setError(failed(err, "That didn't work. Try again."));
      setBusy(false);
    }
  };
  return (
    <form className="mt-3 flex max-w-[420px] flex-col gap-2" onSubmit={submit} noValidate>
      <label htmlFor={id} className="text-[14px] font-semibold">
        {useBackup ? "Backup code" : "Code from your app"}
      </label>
      <input
        id={id}
        className="field-input"
        inputMode={useBackup ? "text" : "numeric"}
        autoComplete={useBackup ? "off" : "one-time-code"}
        value={value}
        onChange={(e) => setValue(useBackup ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
      />
      <button type="submit" className="btn btn-secondary w-full sm:w-auto sm:self-start sm:px-6" disabled={busy}>
        {busy ? "Checking…" : label}
      </button>
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-[14px]">
        <button type="button" className="text-accent-text underline" onClick={() => setUseBackup((b) => !b)}>
          {useBackup ? "Use a code from your app" : "Use a backup code"}
        </button>
        <button type="button" className="text-muted underline" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Message text={error} alert />
    </form>
  );
}

function AuthenticatorBlock({ totp, onChanged }: { totp: PersonSecurity["totp"]; onChanged: () => void }) {
  const codeId = useId();
  const [step, setStep] = useState<"idle" | "setup" | "codes" | "off" | "new-codes">("idle");
  const [setup, setSetup] = useState<{ secret: string; svg: string | null } | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setError(null);
    setStep("setup");
    try {
      const s = await api.totpSetup();
      const svg = await import("qrcode")
        .then((QR) => QR.toString(s.otpauthUrl, { type: "svg", margin: 4, errorCorrectionLevel: "M", color: { dark: "#141619", light: "#FFFFFF" } }))
        .catch(() => null);
      setSetup({ secret: s.secret, svg });
    } catch (err) {
      setError(failed(err, "We couldn't start that. Try again."));
      setStep("idle");
    }
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code from your app.");
    setBusy(true);
    try {
      setCodes((await api.totpConfirm(code)).backupCodes);
      setStep("codes");
    } catch (err) {
      setError(failed(err, "That code didn't work. Try again."));
    } finally {
      setBusy(false);
    }
  };
  const done = () => {
    setStep("idle");
    setSetup(null);
    setCode("");
    onChanged();
  };

  if (step === "codes") {
    return (
      <Block title="Authenticator app">
        <BackupCodeList codes={codes} onDone={done} />
      </Block>
    );
  }

  if (step === "setup") {
    return (
      <Block title="Authenticator app">
        <p className="text-pretty text-[14px] leading-snug">Scan this with an authenticator app (1Password, Google Authenticator or similar), then enter the code it shows.</p>
        {setup ? (
          <>
            <div
              role="img"
              aria-label="QR code for your authenticator app"
              data-testid="person-totp-qr"
              className="mt-3 h-[180px] w-[180px] [&>svg]:h-full [&>svg]:w-full"
              dangerouslySetInnerHTML={setup.svg ? { __html: setup.svg } : undefined}
            />
            <p className="mt-2 text-[13px] text-muted">Can't scan it? Enter this key:</p>
            <code className="mt-1 block break-all rounded-[8px] bg-soft px-2 py-1.5 text-[14px] tracking-[0.06em]" data-testid="person-totp-secret">
              {groupKey(setup.secret)}
            </code>
            <form className="mt-3 flex max-w-[420px] flex-col gap-2" onSubmit={confirm} noValidate>
              <label htmlFor={codeId} className="text-[14px] font-semibold">
                6-digit code
              </label>
              <input
                id={codeId}
                className="field-input text-center text-[20px] tracking-[0.3em] tabular-nums"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button type="submit" className="btn btn-primary w-full sm:w-auto sm:self-start sm:px-6" disabled={busy}>
                {busy ? "Checking…" : "Turn it on"}
              </button>
            </form>
          </>
        ) : (
          <p role="status" className="mt-3 text-muted">
            Loading…
          </p>
        )}
        <Message text={error} alert />
        <button type="button" className="mt-3 text-[14px] text-muted underline" onClick={() => setStep("idle")}>
          Cancel
        </button>
      </Block>
    );
  }

  if (!totp.on) {
    return (
      <Block title="Authenticator app">
        <p className="text-pretty text-[15px] leading-snug">Off. Turn it on to be asked for a code from the app after your password or a sign‑in link.</p>
        <button type="button" className="btn btn-secondary mt-3 w-full sm:w-auto sm:self-start sm:px-6" onClick={() => void start()}>
          Set up an authenticator app
        </button>
        <Message text={error} alert />
      </Block>
    );
  }

  return (
    <Block title="Authenticator app">
      <p className="text-[15px] leading-snug">
        On. {totp.backupCodesLeft} backup {totp.backupCodesLeft === 1 ? "code" : "codes"} left.
      </p>
      {step === "off" ? (
        <CodeConfirm
          label="Turn it off"
          onCancel={() => setStep("idle")}
          onConfirm={async (input) => {
            await api.totpDisable(input);
            done();
          }}
        />
      ) : step === "new-codes" ? (
        <CodeConfirm
          label="Make new backup codes"
          onCancel={() => setStep("idle")}
          onConfirm={async (input) => {
            setCodes((await api.newBackupCodes(input)).backupCodes);
            setStep("codes");
          }}
        />
      ) : (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn btn-secondary w-full sm:w-auto sm:self-start sm:px-6" onClick={() => setStep("new-codes")}>
            New backup codes
          </button>
          <button type="button" className="btn btn-secondary w-full sm:w-auto sm:self-start sm:px-6" onClick={() => setStep("off")}>
            Turn off
          </button>
        </div>
      )}
    </Block>
  );
}
